import { z } from "zod";

import {
  assertSchemaKeysAreFree,
  RESERVED_EDGE_KEYS,
} from "../store/reserved-keys";
import { normalizeTargetMap, validateTargetMapEntries } from "./edge-endpoints";
import { assertJsonValue } from "./json-value";
import {
  EDGE_TYPE_BRAND,
  type EdgeTargets,
  type EdgeType,
  type KindAnnotations,
  type NodeType,
} from "./types";

// ============================================================
// Edge Factory Options
// ============================================================

/**
 * Options for defining an edge type.
 */
export type DefineEdgeOptions<
  S extends z.ZodObject<z.ZodRawShape>,
  From extends readonly NodeType[] | undefined = undefined,
  To extends EdgeTargets | undefined = undefined,
> = Readonly<{
  /** Zod schema for edge properties (defaults to empty object) */
  schema?: S;
  /** Optional description for documentation */
  description?: string;
  /**
   * Consumer-owned structured annotations for the kind.
   *
   * Stored in the canonical schema and **participates in schema hashing** —
   * any change bumps the schema version and shows up as a `safe`-severity
   * diff in `getSchemaChanges`. See `KindAnnotations` for the contract.
   */
  annotations?: KindAnnotations;
  /** Node types that can be the source of this edge (domain constraint) */
  from?: From;
  /** Node types or source-to-target mapping for this edge (range constraint) */
  to?: To;
}>;

// ============================================================
// Empty Schema
// ============================================================

const EMPTY_SCHEMA = z.object({});
type EmptySchema = typeof EMPTY_SCHEMA;

// ============================================================
// Edge Factory
// ============================================================

function validateSchemaKeys(
  schema: z.ZodObject<z.ZodRawShape>,
  name: string,
): void {
  assertSchemaKeysAreFree("Edge", name, schema, RESERVED_EDGE_KEYS);
}

/**
 * Creates an edge type definition.
 *
 * @example
 * ```typescript
 * // Edge with no properties
 * const hasEpisode = defineEdge("hasEpisode");
 *
 * // Edge with properties
 * const employedAt = defineEdge("employedAt", {
 *   schema: z.object({
 *     isPrimary: z.boolean().default(true),
 *     startedAt: z.date(),
 *   }),
 *   description: "Employment relationship",
 * });
 *
 * // Edge with domain/range constraints (can be used directly in defineGraph)
 * const worksAt = defineEdge("worksAt", {
 *   schema: z.object({ role: z.string() }),
 *   from: [Person],
 *   to: [Company],
 * });
 *
 * // Edge with source-dependent target constraints
 * const dependsOn = defineEdge("dependsOn", {
 *   from: [Task, Course],
 *   to: {
 *     Task: [Task],
 *     Course: [Course],
 *   },
 * });
 * ```
 */
// Overload: no options - returns edge without domain/range
export function defineEdge<K extends string>(name: K): EdgeType<K, EmptySchema>;

// Overload: options with Cartesian from/to arrays
export function defineEdge<
  K extends string,
  S extends z.ZodObject<z.ZodRawShape>,
  From extends readonly NodeType[],
  To extends readonly NodeType[],
>(
  name: K,
  options: DefineEdgeOptions<S, From, To> & { from: From; to: To },
): EdgeType<K, S, From, To>;

// Overload: options with source-dependent target mapping
export function defineEdge<
  K extends string,
  S extends z.ZodObject<z.ZodRawShape>,
  From extends readonly NodeType[],
  To extends Record<From[number]["kind"], readonly [NodeType, ...NodeType[]]>,
>(
  name: K,
  options: DefineEdgeOptions<S, From, To> & { from: From; to: To },
): EdgeType<K, S, From, To>;

// Overload: options without from/to - returns edge without domain/range
export function defineEdge<
  K extends string,
  S extends z.ZodObject<z.ZodRawShape>,
>(name: K, options: DefineEdgeOptions<S, undefined, undefined>): EdgeType<K, S>;

// Implementation
export function defineEdge<
  K extends string,
  S extends z.ZodObject<z.ZodRawShape>,
  From extends readonly NodeType[] | undefined,
  To extends EdgeTargets | undefined,
>(
  name: K,
  options?: DefineEdgeOptions<S, From, To>,
): EdgeType<K, S, From, To> | EdgeType<K, EmptySchema> {
  const schema = options?.schema ?? EMPTY_SCHEMA;
  validateSchemaKeys(schema, name);
  if (options?.annotations !== undefined) {
    assertJsonValue(options.annotations, "annotations", `Edge "${name}"`);
  }

  let resolvedTo: To | undefined = options?.to;
  if (options?.to !== undefined && !Array.isArray(options.to)) {
    validateTargetMapEntries(name, options.from, options.to);
    resolvedTo = normalizeTargetMap(options.to) as To;
  }

  return Object.freeze({
    [EDGE_TYPE_BRAND]: true as const,
    kind: name,
    schema,
    description: options?.description,
    annotations: options?.annotations,
    from: options?.from,
    to: resolvedTo,
  }) as EdgeType<K, S, From, To> | EdgeType<K, EmptySchema>;
}
